import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';

export type AnalyticsEventMap = {
  auth_sign_in_success: undefined;
  auth_sign_in_failed: {
    reason?: string;
  };
  auth_sign_out: undefined;
  quiz_answer_submitted: {
    course_id: string;
    content_id: string;
    quiz_id: string;
    answer_value: 'true' | 'false' | 'skipped';
    is_correct: boolean;
    attempt_type: 'initial' | 'retry';
    question_title?: string;
    question_order?: number;
    knowledge_check_type?: string | null;
    remaining_questions?: number;
  };
  quiz_summary_viewed: {
    course_id: string;
    content_id: string;
    score: number;
    passed: boolean;
    total_questions: number;
    correct_answers: number;
    incorrect_answers: number;
    unanswered: number;
  };
  course_module_completed: {
    course_id: string;
    course_title?: string;
    content_id: string;
    content_title?: string;
    content_order?: number | null;
    content_type?: string | null;
    credit_hours?: number | null;
    duration_secs?: number | null;
  };
  course_completed: {
    course_id: string;
    course_title?: string;
    total_modules: number;
    completed_modules: number;
    progress_percent?: number | null;
    duration_secs?: number | null;
    certificate_available?: boolean;
  };
  course_completion_request_submitted: {
    course_ids: string[];
    content_ids: string[];
    total_items: number;
    additional_information?: boolean;
  };
  binge_mode_enable_clicked: {
    course_id: string;
    content_id: string;
    progress_percent: number;
    playback_position_secs: number;
  };
  binge_mode_disable_clicked: {
    course_id: string;
    content_id: string;
    progress_percent: number;
    playback_position_secs: number;
  };
  binge_mode_sync_clicked: {
    course_id: string;
    content_id: string;
    progress_percent: number;
    playback_position_secs: number;
  };
};

type OptionalPayloadEvent = {
  [K in keyof AnalyticsEventMap]: AnalyticsEventMap[K] extends undefined
    ? K
    : never;
}[keyof AnalyticsEventMap];

type RequiredPayloadEvent = Exclude<
  keyof AnalyticsEventMap,
  OptionalPayloadEvent
>;

export type AnalyticsEventName = keyof AnalyticsEventMap;

type CaptureAnalyticsEvent = {
  <E extends OptionalPayloadEvent>(
    event: E,
    properties?: AnalyticsEventMap[E],
  ): void;
  <E extends RequiredPayloadEvent>(
    event: E,
    properties: AnalyticsEventMap[E],
  ): void;
};

/**
 * PostHog 이벤트를 캡쳐하는 hook을 제공합니다.
 */
export function useCaptureAnalyticsEvent(): CaptureAnalyticsEvent {
  const posthog = usePostHog();

  const capture = useCallback(
    <E extends keyof AnalyticsEventMap>(
      event: E,
      properties?: AnalyticsEventMap[E],
    ) => {
      posthog?.capture(
        event,
        properties as Record<string, unknown> | undefined,
      );
    },
    [posthog],
  );

  // 타입 오버로드를 적용
  return capture as CaptureAnalyticsEvent;
}
